import { useEffect, useMemo, useState } from "react";
import { DayOfWeek, Grade, GradeDayPeriods, Period, WORKING_DAYS } from "../types";
import { fetchGrades } from "../services/gradesService";
import { fetchAllGradePeriods, fetchPeriods, saveGradePeriods } from "../services/periodsService";

const sameIds = (a: number[] = [], b: number[] = []) => a.length === b.length && a.every((id) => b.includes(id));
const sameDays = (a: GradeDayPeriods = {}, b: GradeDayPeriods = {}) => WORKING_DAYS.every((day) => sameIds(a[day], b[day]));
const copyDays = (days: GradeDayPeriods = {}): GradeDayPeriods =>
  Object.fromEntries(WORKING_DAYS.map((day) => [day, [...(days[day] ?? [])]]));

/**
 * Which periods each grade uses on each working day, keyed by grade code. Changes stay in `draft`
 * until `saveAll`, which sends one request per changed grade; `saved` is the last state the server
 * confirmed.
 */
export const useGradePeriods = () => {
  const [grades, setGrades] = useState<Grade[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [saved, setSaved] = useState<Record<string, GradeDayPeriods>>({});
  const [draft, setDraft] = useState<Record<string, GradeDayPeriods>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [loadedGrades, loadedPeriods, byGrade] = await Promise.all([fetchGrades(), fetchPeriods(), fetchAllGradePeriods()]);
        const all = Object.fromEntries(loadedGrades.map((grade) => [grade.code, copyDays(byGrade[grade.code])]));
        setGrades(loadedGrades);
        setPeriods([...loadedPeriods].sort((a, b) => a.startTime.localeCompare(b.startTime)));
        setSaved(all);
        setDraft(JSON.parse(JSON.stringify(all)));
      } catch (err) {
        setError("Failed to load grade periods.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const changedCodes = useMemo(
    () => grades.map((grade) => grade.code).filter((code) => !sameDays(draft[code], saved[code])),
    [grades, draft, saved]
  );

  const setDays = (code: string, change: (days: GradeDayPeriods) => GradeDayPeriods) =>
    setDraft((prev) => ({ ...prev, [code]: change(copyDays(prev[code])) }));

  const toggle = (code: string, day: DayOfWeek, periodId: number) =>
    setDays(code, (days) => {
      const ids = days[day] ?? [];
      return { ...days, [day]: ids.includes(periodId) ? ids.filter((id) => id !== periodId) : [...ids, periodId] };
    });

  /** Ticks every period on the day, or clears the day when all are already ticked. */
  const toggleDay = (code: string, day: DayOfWeek) =>
    setDays(code, (days) => {
      const allTicked = periods.every((period) => (days[day] ?? []).includes(period.id!));
      return { ...days, [day]: allTicked ? [] : periods.map((period) => period.id!) };
    });

  /** Ticks the period on every day, or clears it from every day when it's on all of them. */
  const togglePeriod = (code: string, periodId: number) =>
    setDays(code, (days) => {
      const onAll = WORKING_DAYS.every((day) => (days[day] ?? []).includes(periodId));
      return Object.fromEntries(
        WORKING_DAYS.map((day) => {
          const ids = (days[day] ?? []).filter((id) => id !== periodId);
          return [day, onAll ? ids : [...ids, periodId]];
        })
      );
    });

  /** Monday's periods for Tuesday to Saturday. */
  const copyMondayToAll = (code: string) =>
    setDays(code, (days) => Object.fromEntries(WORKING_DAYS.map((day) => [day, [...(days.MONDAY ?? [])]])));

  /** One grade's whole week to the other grades. */
  const copyToGrades = (fromCode: string, toCodes: string[]) =>
    setDraft((prev) => {
      const next = { ...prev };
      toCodes.forEach((code) => {
        next[code] = copyDays(prev[fromCode]);
      });
      return next;
    });

  const discard = () => {
    setDraft(JSON.parse(JSON.stringify(saved)));
    setError(null);
  };

  /** Saves every changed grade; returns how many failed (0 = all saved). */
  const saveAll = async () => {
    setSaving(true);
    setError(null);
    let failed = 0;
    for (const code of changedCodes) {
      try {
        const result = copyDays(await saveGradePeriods(code, draft[code]));
        setSaved((prev) => ({ ...prev, [code]: result }));
        setDraft((prev) => ({ ...prev, [code]: copyDays(result) }));
      } catch (err) {
        failed++;
      }
    }
    if (failed > 0) setError(`Failed to save ${failed} grade(s). Their changes are still on screen.`);
    setSaving(false);
    return failed;
  };

  return {
    grades,
    periods,
    draft,
    changedCodes,
    loading,
    saving,
    error,
    actions: { toggle, toggleDay, togglePeriod, copyMondayToAll, copyToGrades, discard, saveAll },
  };
};
